import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DateTime } from 'luxon';

import { DOMAIN, PAYMENT_METHODS } from '@autopass-web/domain';
import {
  DpassSignupSection,
  useDpassSignup,
} from '@autopass-web/feature-dpass-signup';
import {
  Alert,
  LoadingMochi,
  MochiButton,
} from '@autopass-web/mochi-component';
import { useBarcodeCarrierValidate } from '@autopass-web/mochi-payment/services/use-valildate-barcode-carrier';
import { PaymentOptions } from '@autopass-web/mochi-payment-module';
import { axiosAutoPassErrorConverter } from '@autopass-web/mochi-utils';

import {
  createApplePaySession,
  createCreditCardSession,
  createEasyWalletSession,
  createJkoPaySession,
  createLinePaySession,
} from '../../services/payments';
import styles from './index.module.scss';

import type { PaymentInfo, Receipt } from '../../services/payments';
import type { PaymentMethodInfo } from '@autopass-web/mochi-payment-module';
import type { PaymentOptionsControlRef } from '@autopass-web/mochi-payment-module';
import type { AutopassAxiosError } from '@autopass-web/mochi-utils';
import type { ParkingOrder, PaymentMethodType } from '@autopass-web/types';
import type {
  CarrierMobileReceiptInfo,
  DonateReceiptInfo,
} from 'libs/mochi-payment-module/src/modules/receipt-config/receipt-types';

import { PageContainer } from '#/guest-aps/components/page-container';
import { PATH } from '#/guest-aps/config';
import { PaymentInfoCard } from '#/guest-aps/features/payment/payment-info-card';
import { PaymentPriceRateHintDialog } from '#/guest-aps/features/payment/payment-price-rate-hint-dialog';
import { PaymentTicketAuthExpiredDialog } from '#/guest-aps/features/payment/payment-ticket-auth-expired-dialog';
import { PaymentTicketPriceSessionTimeoutDialog } from '#/guest-aps/features/payment/payment-ticket-price-session-timeout-dialog';
import { useAmplitudePaymentPageView } from '#/guest-aps/hooks/use-amplitude-payment-pageview';
import { useParkingTicketRenew } from '#/guest-aps/hooks/use-parking-ticket-renew';
import { useTicketAuthExpiredDialogDisclosure } from '#/guest-aps/hooks/use-ticket-auth-expired-dialog-disclosure';
import amplitude, { AMPLITUDE_KEY } from '#/guest-aps/services/amplitude';
import {
  DPASS_SIGNUP_FROM,
  dpassAxiosInstance,
  isDpassSignupVisible,
} from '#/guest-aps/services/dpass-register';
import { createTaiwanPaySession } from '#/guest-aps/services/payments/taiwan-pay';
import { sessionStorageController } from '#/guest-aps/services/storages';

export const PaymentPage = () => {
  const [modalAlertErrorMessage, setModalAlertErrorMessage] = useState<
    string | null
  >(null);
  const { validateBarcodeCarrierMutateAsync } = useBarcodeCarrierValidate();
  const { renew, parkingTicket, renewStatus } = useParkingTicketRenew();
  const apsPartnerTemplate = sessionStorageController.getApsPartner();
  const parkinglot = sessionStorageController.getParkinglot();
  const { isTicketAuthExpiredDialogOpen, onTicketAuthExpiredDialogClose } =
    useTicketAuthExpiredDialogDisclosure();
  const [isTicketTimeoutDialogOpen, setIsTicketTimeoutDialogOpen] =
    useState<boolean>(false);
  const [
    isPaymentPriceRateHintDialogOpen,
    setIsPaymentPriceRateHintDialogOpen,
  ] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<PaymentMethodType | null>(null);

  const dpassSignupVisible = isDpassSignupVisible({
    parkinglot,
    paymentMethod: selectedPaymentMethod,
  });

  const [agreedToDpassMembership, setAgreedToDpassMembership] =
    useState(dpassSignupVisible);
  const isDpassMembershipChecked =
    dpassSignupVisible && agreedToDpassMembership;

  const {
    isDpassDialogOpen,
    isDpassSubmitting,
    onDpassDialogClose,
    onDpassDialogSubmit,
    requestDpassSignup,
  } = useDpassSignup({
    client: dpassAxiosInstance,
    from: DPASS_SIGNUP_FROM,
    onDialogDismiss: () => setAgreedToDpassMembership(false),
    onSignupResult: ({ email, status }) => {
      amplitude.paymentDpassSignupSubmit({
        ticketId: parkingTicket?.data?.id ?? '',
        plateNumber: parkingTicket?.data?.plate_number ?? '',
        email,
        status,
        partnerId: parkinglot?.partner_id,
      });
    },
  });

  useAmplitudePaymentPageView({
    ticketId: parkingTicket?.data?.id,
    plateNumber: parkingTicket?.data?.plate_number,
  });

  const controlRef = useRef<PaymentOptionsControlRef>();

  const handlePaymentSubmit = async ({
    paymentInfo,
    receipt,
    paymentMethod,
    email,
  }: {
    paymentInfo: PaymentInfo;
    receipt: Receipt;
    paymentMethod: PaymentMethodInfo;
    email?: string;
  }) => {
    setIsSubmitting(true);

    try {
      let webLink: string | undefined = undefined;

      switch (paymentMethod.paymentMethod) {
        case PAYMENT_METHODS.APPLE_PAY: {
          await new Promise((resolve, reject) => {
            createApplePaySession(
              { paymentInfo, receipt },
              {
                onSuccess: (result: ParkingOrder) => {
                  setTimeout(() => {
                    navigate(PATH.SUCCESS, {
                      replace: true,
                      state: { data: result },
                    });

                    resolve(result);
                  }, 1500);
                },
                onError: (error: unknown) => {
                  reject(error);
                },
                onCancel: () => {
                  reject({ message: '使用 Apple pay 時取消交易' });
                },
              },
            );
          });
          break;
        }
        case PAYMENT_METHODS.LINE_PAY: {
          webLink = await createLinePaySession({
            paymentInfo,
            receipt,
            email,
          });
          break;
        }
        case PAYMENT_METHODS.JKOPAY: {
          webLink = await createJkoPaySession({
            paymentInfo,
            receipt,
            email,
          });

          break;
        }

        case PAYMENT_METHODS.EASY_WALLET: {
          webLink = await createEasyWalletSession({
            paymentInfo,
            receipt,
            email,
          });
          break;
        }

        case PAYMENT_METHODS.TAIWAN_PAY: {
          webLink = await createTaiwanPaySession({
            paymentInfo,
            receipt,
            email,
          });
          break;
        }

        case PAYMENT_METHODS.CREDIT_CARD: {
          const creditCard = paymentMethod;

          await createCreditCardSession({
            paymentInfo,
            receipt,
            email,
            cardInfo: creditCard.paymentInfo,
          });
          break;
        }

        default:
          break;
      }

      if (webLink) {
        window.location.href = webLink;
      }
    } catch (err) {
      setIsSubmitting(false);
      console.error('handle payment submit', err);

      const isAxiosError = axios.isAxiosError(err);

      if (isAxiosError === false) {
        throw err;
      }
      const axiosError = axiosAutoPassErrorConverter(err as AutopassAxiosError);

      throw axiosError;
    }
  };

  if (parkingTicket == null || renewStatus === 'pending')
    return <LoadingMochi show />;

  const {
    merchant: { uid: merchantUid },
    data: {
      id,
      current_price_period,
      plate_number,
      checked_in_at,
      checked_out_at,
      request_amount,
      should_leave_before,
    },
  } = parkingTicket;

  const amplitudeEventParams = { ticketId: id, plateNumber: plate_number };

  const paymentInfo = {
    id,
    plateNumber: plate_number,
    amount: request_amount,
    merchantUid: merchantUid,
  };

  const supportedPaymentMethods = (
    apsPartnerTemplate?.attributes?.support_payments ?? [
      PAYMENT_METHODS.CREDIT_CARD,
      PAYMENT_METHODS.APPLE_PAY,
      PAYMENT_METHODS.LINE_PAY,
      PAYMENT_METHODS.JKOPAY,
      PAYMENT_METHODS.TAIWAN_PAY,
    ]
  ).filter((type) => {
    if (type === 'easy_wallet') {
      return request_amount <= 30000;
    }

    return true;
  });

  const ctaButtonText = `${isDpassMembershipChecked ? '同意並' : ''}付款 ${Math.round(+request_amount)} 元`;

  const onSubmit = async () => {
    try {
      const now = DateTime.now();
      const shouldLeaveBefore = DateTime.fromISO(should_leave_before);

      if (now >= shouldLeaveBefore) {
        amplitude.paymentTimeoutDialogPageView(amplitudeEventParams);
        setIsTicketTimeoutDialogOpen(true);
        return;
      }

      const res = await controlRef.current?.submit();
      const {
        payment: [payment],
        receipt: [receiptInfo],
      } = res;

      const receipt: Receipt = (() => {
        switch (receiptInfo.type) {
          case 'b2b':
            return {
              name: '',
              phone: '',
              type: 'b2b',
              ubn: receiptInfo.ubn,
            };
          case 'carrier_member':
            return {
              name: '',
              phone: '',
              type: 'carrier_member',
            };
          case 'carrier_mobile_barcode':
            return {
              name: '',
              phone: '',
              barcode: (receiptInfo as CarrierMobileReceiptInfo).barcode,
              type: 'carrier_mobile_barcode',
            };
          case 'donate':
            return {
              name: '',
              phone: '',
              love_code: (receiptInfo as DonateReceiptInfo).love_code,
              type: 'donate',
            };
          case 'payment_carrier':
            return {
              name: '',
              phone: '',
              type: 'payment_carrier',
            };
        }
      })();

      let email: string | undefined =
        receiptInfo.type !== 'payment_carrier' && receiptInfo.email
          ? receiptInfo.email
          : undefined;

      if (isDpassMembershipChecked) {
        const resolvedEmail = await requestDpassSignup(email);
        if (resolvedEmail == null) return;
        email = resolvedEmail;
      }

      amplitude.track('付款設定頁-付款_點擊', {
        停車憑證id: id,
        車牌號碼: plate_number,
        發票形式: receipt.type,
        付款方式: payment.paymentMethod,
        email,
      });

      await handlePaymentSubmit({
        paymentInfo,
        email,
        receipt,
        paymentMethod: payment,
      });
    } catch (err) {
      setModalAlertErrorMessage(err.message);
    }
  };

  return (
    <>
      <PageContainer>
        <Alert
          type="full"
          onClose={() => {
            setModalAlertErrorMessage(null);
          }}
          show={modalAlertErrorMessage != null}
          className={styles['alert']}
        >
          {modalAlertErrorMessage}
        </Alert>
        <div className={styles.wrapper}>
          <div className={styles.title}>付款 {plate_number} 停車費</div>
          <PaymentInfoCard
            checkedInAt={checked_in_at}
            checkedOutAt={checked_out_at}
            requestAmount={request_amount}
            shouldLeaveBefore={should_leave_before}
            currentPricePeriod={current_price_period}
            amplitudeEventParams={amplitudeEventParams}
          />
          <PaymentOptions
            eventRegistry={{
              paymentOptionClick: (params) => {
                amplitude.track('付款設定頁-選擇付款方式_點擊', params);
              },
              receiptOptionClick: (params) => {
                amplitude.track('付款設定頁-選擇發票方式_點擊', params);
              },
            }}
            availMethods={supportedPaymentMethods.map((value) => ({
              value,
              available: true,
            }))}
            analKey={AMPLITUDE_KEY}
            controlRef={controlRef}
            onMethodChange={(method) => {
              setSelectedPaymentMethod(method ?? null);
            }}
            validateBarcode={async (input: string) => {
              try {
                const res = await validateBarcodeCarrierMutateAsync({
                  barcode: input,
                });

                return res.data.data.status === 'valid';
              } catch {
                return false;
              }
            }}
          />

          {dpassSignupVisible && (
            <DpassSignupSection
              checked={isDpassMembershipChecked}
              onCheckedChange={setAgreedToDpassMembership}
              isDialogOpen={isDpassDialogOpen}
              onDialogClose={onDpassDialogClose}
              onDialogSubmit={onDpassDialogSubmit}
              isSubmitting={isDpassSubmitting}
              dpassAppUrl={DOMAIN.DPASS_URL}
              termsOfServiceUrl={`${DOMAIN.AUTOPASS_URL}/terms`}
              privacyPolicyUrl={`${DOMAIN.AUTOPASS_URL}/privacy`}
              logger={amplitude.track}
              eventProps={{ page: '付款設定頁' }}
            />
          )}

          <div className="w-[80%] mx-auto flex flex-col gap-4 mt-[20px]">
            <MochiButton
              type="button"
              variant="solid-round"
              size="full"
              onClick={onSubmit}
              isLoading={isSubmitting}
            >
              {ctaButtonText}
            </MochiButton>
          </div>
        </div>
      </PageContainer>
      <PaymentTicketAuthExpiredDialog
        isOpen={isTicketAuthExpiredDialogOpen}
        onButtonClick={() => {
          amplitude.paymentTicketExpireDialogOnClick();
          onTicketAuthExpiredDialogClose();
          renew();
        }}
      />
      <PaymentTicketPriceSessionTimeoutDialog
        isOpen={isTicketTimeoutDialogOpen}
        onButtonClick={() => {
          setIsTicketTimeoutDialogOpen(false);
          renew();
        }}
      />
      <PaymentPriceRateHintDialog
        messages={current_price_period}
        isOpen={isPaymentPriceRateHintDialogOpen}
        onButtonClick={() => setIsPaymentPriceRateHintDialogOpen(false)}
      />
    </>
  );
};
